import React, { useState, useEffect } from 'react'
import { 
  Settings, 
  Save, 
  Upload, 
  AlertCircle, 
  CheckCircle, 
  Cloud, 
  HardDrive,
  Key,
  Database
} from 'lucide-react'
import { useAuth } from '@/context/AuthContext'
import { apiRequest } from '@/services/api'
import toast from 'react-hot-toast'

const SystemSettings = () => {
  const { hasRole } = useAuth()
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [storageConfig, setStorageConfig] = useState({
    storage_backend: 'local',
    gcs_bucket_name: '',
    gcs_project_id: '',
    gcs_credentials_json: '',
    gcs_credentials_configured: false
  })
  const [credentialsFile, setCredentialsFile] = useState(null)

  // Check if user has admin access
  if (!hasRole('admin')) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="mx-auto h-12 w-12 flex items-center justify-center rounded-full bg-red-100">
            <AlertCircle className="h-6 w-6 text-red-600" />
          </div>
          <h3 className="mt-2 text-sm font-medium text-gray-900">Access Denied</h3>
          <p className="mt-1 text-sm text-gray-500">
            You need administrator privileges to access system settings.
          </p>
        </div>
      </div>
    )
  }

  useEffect(() => {
    fetchStorageConfig()
  }, [])

  const fetchStorageConfig = async () => {
    try {
      setLoading(true)
      const response = await apiRequest.get('/system-settings/storage/config')
      setStorageConfig(response.data)
    } catch (error) {
      console.error('Error fetching storage config:', error)
      toast.error('Failed to load storage configuration')
    } finally {
      setLoading(false)
    }
  }

  const handleStorageBackendChange = (backend) => {
    setStorageConfig(prev => ({
      ...prev,
      storage_backend: backend
    }))
  }

  const handleInputChange = (field, value) => {
    setStorageConfig(prev => ({
      ...prev,
      [field]: value
    }))
  }

  const handleCredentialsFileChange = (event) => {
    const file = event.target.files[0]
    if (file) {
      setCredentialsFile(file)
      const reader = new FileReader()
      reader.onload = (e) => {
        try {
          const jsonContent = e.target.result
          JSON.parse(jsonContent) // Validate JSON
          setStorageConfig(prev => ({
            ...prev,
            gcs_credentials_json: jsonContent
          }))
          toast.success('Credentials file loaded successfully')
        } catch (error) {
          toast.error('Invalid JSON file')
          setCredentialsFile(null)
        }
      }
      reader.readAsText(file)
    }
  }

  const validateGCSConfig = () => {
    if (storageConfig.storage_backend === 'gcs') {
      if (!storageConfig.gcs_bucket_name) {
        toast.error('GCS bucket name is required')
        return false
      }
      if (!storageConfig.gcs_project_id) {
        toast.error('GCS project ID is required')
        return false
      }
      if (!storageConfig.gcs_credentials_json) {
        toast.error('GCS credentials are required')
        return false
      }
    }
    return true
  }

  const handleSave = async () => {
    if (!validateGCSConfig()) {
      return
    }

    try {
      setSaving(true)
      await apiRequest.post('/system-settings/storage/config', storageConfig)
      toast.success('Storage configuration saved successfully')
      await fetchStorageConfig() // Refresh the config
    } catch (error) {
      console.error('Error saving storage config:', error)
      toast.error(error.response?.data?.detail || 'Failed to save storage configuration')
    } finally {
      setSaving(false)
    }
  }

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    )
  }

  return (
    <div className="max-w-4xl mx-auto p-6">
      <div className="bg-white shadow rounded-lg">
        <div className="px-6 py-4 border-b border-gray-200">
          <div className="flex items-center">
            <Settings className="h-6 w-6 text-gray-400 mr-3" />
            <h1 className="text-xl font-semibold text-gray-900">System Settings</h1>
          </div>
          <p className="mt-1 text-sm text-gray-500">
            Configure system-wide settings and file storage options.
          </p>
        </div>

        <div className="p-6">
          {/* Storage Configuration Section */}
          <div className="mb-8">
            <h2 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
              <Database className="h-5 w-5 text-gray-400 mr-2" />
              File Storage Configuration
            </h2>

            {/* Storage Backend Selection */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-3">
                Storage Backend
              </label>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {/* Local Storage */}
                <div 
                  className={`relative rounded-lg border p-4 cursor-pointer transition-colors ${
                    storageConfig.storage_backend === 'local'
                      ? 'border-primary-500 bg-primary-50'
                      : 'border-gray-300 hover:border-gray-400'
                  }`}
                  onClick={() => handleStorageBackendChange('local')}
                >
                  <div className="flex items-center">
                    <HardDrive className="h-6 w-6 text-gray-400 mr-3" />
                    <div>
                      <h3 className="text-sm font-medium text-gray-900">Local Storage</h3>
                      <p className="text-xs text-gray-500">Store files on local filesystem</p>
                    </div>
                  </div>
                  {storageConfig.storage_backend === 'local' && (
                    <CheckCircle className="absolute top-2 right-2 h-5 w-5 text-primary-500" />
                  )}
                </div>

                {/* Google Cloud Storage */}
                <div 
                  className={`relative rounded-lg border p-4 cursor-pointer transition-colors ${
                    storageConfig.storage_backend === 'gcs'
                      ? 'border-primary-500 bg-primary-50'
                      : 'border-gray-300 hover:border-gray-400'
                  }`}
                  onClick={() => handleStorageBackendChange('gcs')}
                >
                  <div className="flex items-center">
                    <Cloud className="h-6 w-6 text-gray-400 mr-3" />
                    <div>
                      <h3 className="text-sm font-medium text-gray-900">Google Cloud Storage</h3>
                      <p className="text-xs text-gray-500">Store files in GCS bucket</p>
                    </div>
                  </div>
                  {storageConfig.storage_backend === 'gcs' && (
                    <CheckCircle className="absolute top-2 right-2 h-5 w-5 text-primary-500" />
                  )}
                </div>

                {/* S3 Storage (Future) */}
                <div className="relative rounded-lg border border-gray-200 p-4 opacity-50">
                  <div className="flex items-center">
                    <Cloud className="h-6 w-6 text-gray-400 mr-3" />
                    <div>
                      <h3 className="text-sm font-medium text-gray-900">Amazon S3</h3>
                      <p className="text-xs text-gray-500">Coming soon</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* GCS Configuration */}
            {storageConfig.storage_backend === 'gcs' && (
              <div className="bg-gray-50 rounded-lg p-6">
                <h3 className="text-md font-medium text-gray-900 mb-4 flex items-center">
                  <Cloud className="h-5 w-5 text-gray-400 mr-2" />
                  Google Cloud Storage Configuration
                </h3>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* Bucket Name */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Bucket Name *
                    </label>
                    <input
                      type="text"
                      value={storageConfig.gcs_bucket_name || ''}
                      onChange={(e) => handleInputChange('gcs_bucket_name', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                      placeholder="my-storage-bucket"
                    />
                  </div>

                  {/* Project ID */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Project ID *
                    </label>
                    <input
                      type="text"
                      value={storageConfig.gcs_project_id || ''}
                      onChange={(e) => handleInputChange('gcs_project_id', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                      placeholder="my-gcp-project"
                    />
                  </div>
                </div>

                {/* Service Account Credentials */}
                <div className="mt-6">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Service Account Credentials *
                  </label>
                  <div className="flex items-center space-x-4">
                    <div className="flex-1">
                      <input
                        type="file"
                        accept=".json"
                        onChange={handleCredentialsFileChange}
                        className="hidden"
                        id="credentials-file"
                      />
                      <label
                        htmlFor="credentials-file"
                        className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 cursor-pointer"
                      >
                        <Upload className="h-4 w-4 mr-2" />
                        Upload JSON Key File
                      </label>
                    </div>
                    {storageConfig.gcs_credentials_configured && (
                      <div className="flex items-center text-green-600">
                        <CheckCircle className="h-4 w-4 mr-1" />
                        <span className="text-sm">Credentials configured</span>
                      </div>
                    )}
                  </div>
                  {credentialsFile && (
                    <p className="mt-2 text-sm text-gray-600">
                      Selected: {credentialsFile.name}
                    </p>
                  )}
                </div>
              </div>
            )}

            {/* Local Storage Info */}
            {storageConfig.storage_backend === 'local' && (
              <div className="bg-blue-50 rounded-lg p-4">
                <div className="flex items-center">
                  <HardDrive className="h-5 w-5 text-blue-400 mr-2" />
                  <div>
                    <h3 className="text-sm font-medium text-blue-900">Local Storage</h3>
                    <p className="text-sm text-blue-700">
                      Files will be stored on the local filesystem. Make sure you have adequate storage space and backup procedures in place.
                    </p>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Save Button */}
          <div className="flex justify-end">
            <button
              onClick={handleSave}
              disabled={saving}
              className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {saving ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Saving...
                </>
              ) : (
                <>
                  <Save className="h-4 w-4 mr-2" />
                  Save Configuration
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

export default SystemSettings
